import React, { useMemo, useRef, useState } from 'react';
import {
  FiCheck,
  FiGlobe,
  FiImage,
  FiLoader,
  FiMove,
  FiSave,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from 'react-icons/fi';
import { api } from '../services/api';
import { getDirectMediaUrlInputValue } from '../utils/mediaUrl';
import AdminTranslationPopoverPortal from './AdminTranslationPopoverPortal';
import './AdminProductImagesTranslation.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
const EMPTY_IMAGES = { cardImageUrl: '', detailImageUrl: '' };
const IMAGE_FIELDS = [
  {
    name: 'cardImageUrl',
    title: 'تصویر کارت محصول',
    badge: 'مربعی ۱:۱',
    previewClass: 'is-square',
    placeholder: 'https://example.com/product-card.webp',
  },
  {
    name: 'detailImageUrl',
    title: 'تصویر صفحه محصول',
    badge: 'افقی ۱۶:۹',
    previewClass: 'is-banner',
    placeholder: 'https://example.com/product-banner.webp',
  },
];

const resolveMediaUrl = (value) => (
  value && value.startsWith('/') ? `${API_URL}${value}` : value
);

const imagesFromTranslation = (translation) => ({
  cardImageUrl: translation?.cardImageUrl || '',
  detailImageUrl: translation?.detailImageUrl || '',
});

const emitSaveState = (productId, locale, fieldPath, state) => {
  window.dispatchEvent(new CustomEvent('haryad:translation-save-state', {
    detail: {
      entityType: 'product',
      entityId: String(productId),
      locale,
      fieldPath,
      state,
    },
  }));
};

const AdminProductImagesTranslation = ({ productId, sourceImages = EMPTY_IMAGES }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locales, setLocales] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [activeLocale, setActiveLocale] = useState('');
  const [images, setImages] = useState(EMPTY_IMAGES);
  const [uploadingField, setUploadingField] = useState('');
  const [savingField, setSavingField] = useState('');
  const [error, setError] = useState('');
  const rootRef = useRef(null);

  const enabledLocales = useMemo(
    () => locales.filter((locale) => locale.enabled !== false && locale.code !== 'fa'),
    [locales]
  );
  const activeLanguage = enabledLocales.find((locale) => locale.code === activeLocale);
  const activeTranslation = translations.find((item) => item.locale === activeLocale);

  const selectLocale = (localeCode, items = translations) => {
    setActiveLocale(localeCode);
    setImages(imagesFromTranslation(items.find((item) => item.locale === localeCode)));
    setUploadingField('');
    setSavingField('');
    setError('');
  };

  const load = async () => {
    const [localeResponse, translationResponse] = await Promise.all([
      api.get('/api/locales'),
      api.get(`/api/products/${productId}/translations`),
    ]);
    const localeItems = Array.isArray(localeResponse.data) ? localeResponse.data : [];
    const translationItems = Array.isArray(translationResponse.data) ? translationResponse.data : [];
    setLocales(localeItems);
    setTranslations(translationItems);
    const firstLocale = localeItems.find((item) => item.enabled !== false && item.code !== 'fa')?.code || '';
    selectLocale(firstLocale, translationItems);
  };

  const handleOpen = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setLoading(true);
    setError('');
    try {
      await load();
    } catch (loadError) {
      setError(loadError.data?.message || loadError.message || 'دریافت تصاویر زبان‌ها انجام نشد');
    } finally {
      setLoading(false);
    }
  };

  const updateLocalTranslation = (localeCode, fieldName, value) => {
    setTranslations((items) => {
      const existing = items.find((item) => item.locale === localeCode);
      const updated = {
        ...(existing || { locale: localeCode, status: 'draft' }),
        [fieldName]: value,
        hasUnpublishedChanges: true,
      };
      return existing
        ? items.map((item) => (item === existing ? updated : item))
        : [...items, updated];
    });
  };

  const saveImage = async (fieldName, value) => {
    if (!activeLocale) return;
    const normalizedValue = String(value || '').trim();
    setSavingField(fieldName);
    emitSaveState(productId, activeLocale, fieldName, 'saving');
    setError('');
    try {
      await api.patch(`/api/products/${productId}/translations/${activeLocale}/field`, {
        path: fieldName,
        value: normalizedValue,
      });
      setImages((current) => ({ ...current, [fieldName]: normalizedValue }));
      updateLocalTranslation(activeLocale, fieldName, normalizedValue);
      emitSaveState(productId, activeLocale, fieldName, 'saved');
      window.dispatchEvent(new CustomEvent('haryad:translations-changed', {
        detail: { entityType: 'product', entityId: productId },
      }));
    } catch (saveError) {
      emitSaveState(productId, activeLocale, fieldName, 'error');
      setError(saveError.data?.message || saveError.message || 'ذخیره تصویر انجام نشد');
    } finally {
      setSavingField('');
    }
  };

  const uploadImage = async (event, fieldName) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('فرمت تصویر باید JPG، PNG، WEBP، GIF یا AVIF باشد.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setError('حجم تصویر نباید بیشتر از ۵ مگابایت باشد.');
      return;
    }

    setUploadingField(fieldName);
    emitSaveState(productId, activeLocale, `upload-${fieldName}`, 'saving');
    setError('');
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.upload('/api/products/upload-image', formData);
      const imageUrl = response.data?.imageUrl;
      if (!response.success || !imageUrl) throw new Error('آپلود تصویر انجام نشد');
      await saveImage(fieldName, imageUrl);
    } catch (uploadError) {
      setError(uploadError.data?.message || uploadError.message || 'آپلود تصویر انجام نشد');
    } finally {
      emitSaveState(productId, activeLocale, `upload-${fieldName}`, 'saved');
      setUploadingField('');
    }
  };

  if (!productId) return null;

  return (
    <div className="admin-product-images-translation" ref={rootRef}>
      <button
        type="button"
        className={`admin-product-images-translation__trigger ${open ? 'is-active' : ''}`}
        onClick={handleOpen}
        title="تصاویر محصول در زبان‌های دیگر"
        aria-label="مدیریت تصاویر چندزبانه محصول"
        aria-expanded={open}
      >
        <FiGlobe aria-hidden="true" />
        <FiImage aria-hidden="true" />
        <small>{enabledLocales.length || ''}</small>
      </button>

      {open && (
        <AdminTranslationPopoverPortal
          anchorRef={rootRef}
          className="admin-product-images-translation__popover"
          ariaLabel="تصاویر محصول برای زبان‌های دیگر"
          onClose={() => setOpen(false)}
        >
          {({ dragHandleProps }) => <>
          <header {...dragHandleProps}>
            <div>
              <strong>تصاویر محصول برای زبان‌های دیگر</strong>
              <small>تصاویر هر زبان مستقل‌اند و پس از انتشار همان ترجمه در سایت نمایش داده می‌شوند.</small>
              <small className="admin-translation-popover__drag-hint"><FiMove aria-hidden="true" /> برای جابه‌جایی بکشید</small>
            </div>
            <button type="button" className="ui-close-button" onClick={() => setOpen(false)} aria-label="بستن"><FiX /></button>
          </header>

          {loading ? (
            <div className="admin-product-images-translation__loading"><FiLoader /> در حال دریافت...</div>
          ) : (
            <>
              <div className="admin-product-images-translation__tabs" role="tablist" aria-label="انتخاب زبان تصویر">
                {enabledLocales.map((locale) => {
                  const translation = translations.find((item) => item.locale === locale.code);
                  const filledCount = IMAGE_FIELDS.filter((field) => translation?.[field.name]).length;
                  return (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeLocale === locale.code}
                      className={activeLocale === locale.code ? 'is-active' : ''}
                      key={locale.code}
                      onClick={() => selectLocale(locale.code)}
                    >
                      <b>{locale.code.toUpperCase()}</b>
                      {locale.nativeName}
                      <small>{filledCount.toLocaleString('fa-IR')}/۲</small>
                      {filledCount === 2 ? <FiCheck /> : null}
                    </button>
                  );
                })}
              </div>

              {activeLanguage && (
                <div className="admin-product-images-translation__content">
                  <div className="admin-product-images-translation__source-note">
                    <FiImage /> تصاویر فارسی فقط در صفحهٔ فارسی استفاده می‌شوند و به این زبان منتقل نخواهند شد.
                  </div>

                  <div className="admin-product-images-translation__grid" dir={activeLanguage.direction || 'rtl'}>
                    {IMAGE_FIELDS.map((field) => {
                      const value = images[field.name];
                      const sourceValue = sourceImages[field.name] || '';
                      const busy = uploadingField === field.name || savingField === field.name;
                      const uploadId = `localized-${productId}-${activeLocale}-${field.name}`;
                      return (
                        <section key={field.name}>
                          <div className="admin-product-images-translation__field-head" dir="rtl">
                            <span><strong>{field.title}</strong><small>{field.badge}</small></span>
                            {sourceValue ? <a href={resolveMediaUrl(sourceValue)} target="_blank" rel="noreferrer">مشاهده فارسی</a> : null}
                          </div>

                          <input
                            id={uploadId}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                            onChange={(event) => uploadImage(event, field.name)}
                            disabled={Boolean(uploadingField || savingField)}
                            hidden
                          />
                          <label className={`admin-product-images-translation__upload ${busy ? 'is-busy' : ''}`} htmlFor={uploadId} dir="rtl">
                            {busy ? <FiLoader /> : <FiUploadCloud />}
                            <span>
                              <strong>{busy ? 'در حال ذخیره تصویر...' : 'آپلود تصویر این زبان'}</strong>
                              <small>تا ۵ مگابایت</small>
                            </span>
                          </label>

                          <div className="admin-product-images-translation__url" dir="rtl">
                            <input
                              type="url"
                              dir="ltr"
                              value={getDirectMediaUrlInputValue(value)}
                              placeholder={value.startsWith('/uploads/') ? 'برای جایگزینی، آدرس مستقیم وارد کنید' : field.placeholder}
                              onChange={(event) => setImages((current) => ({ ...current, [field.name]: event.target.value }))}
                            />
                            <button type="button" onClick={() => saveImage(field.name, images[field.name])} disabled={busy}>
                              <FiSave /> ذخیره آدرس
                            </button>
                          </div>

                          {value ? (
                            <div className={`admin-product-images-translation__preview ${field.previewClass}`}>
                              <img src={resolveMediaUrl(value)} alt={`پیش‌نمایش ${field.title} ${activeLanguage.nativeName}`} />
                              <button type="button" onClick={() => saveImage(field.name, '')} disabled={busy} dir="rtl">
                                <FiTrash2 /> حذف
                              </button>
                            </div>
                          ) : (
                            <div className={`admin-product-images-translation__empty ${field.previewClass}`} dir="rtl">
                              برای این زبان تصویری ثبت نشده است.
                            </div>
                          )}
                        </section>
                      );
                    })}
                  </div>

                  <footer dir="rtl">
                    <div>
                      <small>
                        {activeTranslation?.hasUnpublishedChanges
                          ? 'تصاویر در پیش‌نویس ذخیره شده‌اند؛ انتشار نهایی فقط از دکمهٔ ثابت پایین فرم محصول انجام می‌شود.'
                          : activeTranslation?.status === 'published'
                            ? 'نسخه منتشرشده این زبان به‌روز است.'
                            : 'پس از تکمیل ترجمه، از دکمهٔ ثابت پایین فرم محصول استفاده کنید.'}
                      </small>
                      {error && <span>{error}</span>}
                    </div>
                  </footer>
                </div>
              )}

              {!enabledLocales.length && !error && (
                <div className="admin-product-images-translation__empty-languages">زبان دیگری فعال نشده است.</div>
              )}
            </>
          )}

          {error && !activeLanguage && <div className="admin-product-images-translation__error">{error}</div>}
          </>}
        </AdminTranslationPopoverPortal>
      )}
    </div>
  );
};

export default AdminProductImagesTranslation;
